import { DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { appApiUrl, COMPANY_NAME_FC, INVALID_ALL_INPUTS } from 'config'
import { ReactElement, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Api from 'services/api'
import { downloadFile } from 'services/apis/documents'
import { Modal } from 'stories/components'
import { getPrice3decimal, InputValidate, removeComma, REQUIRED_FIELD_ERROR_MESSAGE, serialize } from 'utils'
import { RenderInput } from 'utils/RenderInput'

/**
 * A draw this waiver can be raised against.
 *
 * The picker used to be a list of ledger rows whose option VALUE was the row's Gross Eligible
 * Amount - cumulative cost to date, not the money being advanced - which it then copied into the
 * amount field. Two draws for the same amount collapsed into one option and nothing the form sent
 * said which draw the waiver was about, so the waiver that came back could not be tied to a draw.
 * The option now carries the draw's identity, and the amount comes off the draw on the server.
 */
export interface ILienWaiverDrawOption {
  /**
   * What `id` points at. `''` offers the row as a figure to copy without keying the waiver to it,
   * which is what the REO improvement ledger needs: its rows are not construction draws, and that
   * screen only ever downloads a waiver rather than sending one for signature.
   */
  source: 'request' | 'ledger' | ''
  id: number
  /** How the draw reads in the picker: the address, who it is for, what it is. */
  reference: string
  /** What the draw advances, for the reader to check before they send it. */
  drawnAmount?: number | string | null
  /** Why this draw cannot carry a waiver, when it cannot. Shown, and not selectable. */
  unavailable?: string
}

/** `request:12`. One select value, split apart on submit - see `onSubmit`. */
const optionKey = (option: ILienWaiverDrawOption) => `${option.source}:${option.id}`

const LIEN_WAIVER_SPECIAL_STATES = ['AZ', 'CA', 'FL', 'GA', 'MI', 'MS', 'NV', 'TX', 'UT'] as const

type SpecialState = (typeof LIEN_WAIVER_SPECIAL_STATES)[number]

const resolveSpecialState = (propertyState?: string): SpecialState | null => {
  if (!propertyState) return null
  const code = String(propertyState).trim().toUpperCase()
  return (LIEN_WAIVER_SPECIAL_STATES as readonly string[]).includes(code) ? (code as SpecialState) : null
}

/** Extra inputs needed for state-specific PDFs (not auto-mapped from loan data). */
const getStateExtraInputs = (stateCode: SpecialState | null, waiverType: string) => {
  if (!stateCode) return {}
  if (!waiverType) {
    return {
      stateSection: {
        inputType: 'section',
        title: `${stateCode} Lien Waiver Fields (select Waiver Type)`,
        span: 'full',
      },
    }
  }

  const isProgress = waiverType === 'conditional'
  const fields: Record<string, any> = {
    stateSection: {
      inputType: 'section',
      title: `${stateCode} Lien Waiver Fields`,
      span: 'full',
    },
  }

  const add = (key: string, input: Record<string, any>) => {
    fields[key] = { value: '', error: '', required: true, ...input }
  }

  if ((isProgress && ['CA', 'AZ', 'TX', 'FL', 'GA', 'MI'].includes(stateCode)) || stateCode === 'MS') {
    add('throughDate', { inputType: 'text', type: 'date', title: 'Through Date' })
  }

  if (['AZ', 'TX', 'NV', 'UT', 'GA', 'MS'].includes(stateCode)) {
    add('projectName', {
      inputType: 'text',
      title: stateCode === 'GA' ? 'Title of the Project or Building' : 'Project / Property Name',
      required: false,
    })
  }

  if (['AZ', 'TX'].includes(stateCode)) {
    add('jobNo', { inputType: 'text', title: 'Job No.', required: false })
    add('jobDescription', { inputType: 'textarea', title: 'Job Description', required: false, span: 'full' })
  }

  if (['AZ', 'TX', 'FL', 'GA', 'MS', 'MI'].includes(stateCode)) {
    add('contractedWith', {
      inputType: 'text',
      title:
        stateCode === 'MI'
          ? 'Other Contracting Party'
          : stateCode === 'MS'
            ? 'Hiring Company'
            : stateCode === 'GA'
              ? 'Name of Contractor (Employed By)'
              : 'Person / Company Contracted With',
      required: false,
    })
    add('materialsDescription', {
      inputType: 'textarea',
      title: stateCode === 'MI' ? 'Improvement / Materials Provided' : 'Materials and/or Labor Description',
      span: 'full',
    })
  }

  if (['NV', 'UT'].includes(stateCode)) {
    add('invoicePaymentAppNo', {
      inputType: 'text',
      title: 'Invoice / Payment Application No.',
    })
  }

  if (stateCode === 'UT' && isProgress) {
    add('paymentPeriod', { inputType: 'text', title: 'Payment Period' })
  }

  if (stateCode === 'NV' && !isProgress) {
    add('paymentPeriod', { inputType: 'text', title: 'Payment Period', required: false })
  }

  if (['GA', 'MS'].includes(stateCode)) {
    add('county', { inputType: 'text', title: 'County' })
    add('legalDescription', {
      inputType: 'textarea',
      title: 'Legal Description',
      required: false,
      span: 'full',
    })
  }

  if (stateCode === 'MI') {
    add('claimantAddress', { inputType: 'text', title: 'Claimant Address', span: 'full' })
    add('claimantPhone', { inputType: 'text', title: 'Claimant Telephone', required: false })
  }

  if (stateCode === 'CA' && isProgress) {
    add('priorUnpaidWaiverDates', {
      inputType: 'text',
      title: 'Date(s) of prior unpaid conditional waiver and release',
      required: false,
      span: 'full',
    })
    add('priorUnpaidWaiverAmounts', {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Amount(s) of unpaid progress payment(s)',
      prefix: '$',
      required: false,
    })
  }

  return fields
}

export const ConstructionLienWaiver = (props: {
  /** The draws a waiver can be raised against, in the order the screen lists them. */
  data: ILienWaiverDrawOption[]
  button?: ReactElement
  titleOkay?: string
  initiallyOpen?: boolean
  onSubmitPayload?: (payload: Record<string, any>) => Promise<void>
  onClosed?: () => void
}) => {
  const { data, titleOkay = 'Download', initiallyOpen = false, onSubmitPayload, onClosed } = props
  const [isOpen, setIsOpen] = useState(!!initiallyOpen)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [action, setAction] = useState('')
  const loan = useSelector((state: any) => state.loan)
  const propertyStateCode = useMemo(() => resolveSpecialState(loan?.propertyState), [loan?.propertyState])

  const drawOptions = useMemo(() => (data || []).filter((item) => Number(item?.id) > 0), [data])

  const defaultInputs = (waiverType = '') => {
    const orderOpts: Record<string, string> = {}
    drawOptions.forEach((item) => {
      const amount = item.drawnAmount == null || item.drawnAmount === '' ? null : removeComma(item.drawnAmount)
      const money = amount === null ? 'amount not recorded' : `$${getPrice3decimal(amount)}`
      orderOpts[optionKey(item)] = [`${item.id} - ${item.reference} - ${money}`, item.unavailable]
        .filter(Boolean)
        .join(' - ')
    })
    // The escape hatch the form has always had: a waiver that belongs to no draw on file. It stays
    // the loan-level record, and its amount is the only one still typed by hand.
    orderOpts['other'] = 'Other (not a draw on file)'

    const base: Record<string, any> = {
      type: {
        inputType: 'select',
        title: 'Waiver Type',
        options: {
          conditional: 'Conditional Lien Waiver',
          unConditional: 'Unconditional Lien Waiver of Final Payment',
        },
        hasDefaultOption: true,
        value: waiverType || '',
        required: true,
      },
      orderNo: {
        inputType: 'select',
        title: 'Order ID',
        options: orderOpts,
        hasDefaultOption: true,
        sort: false,
        value: '',
        error: '',
        required: true,
      },
      drawnAmount: {
        inputType: 'text',
        type: 'thousandSep',
        title: 'Total Drawn Amount',
        prefix: '$',
        error: '',
        value: '',
        required: true,
        disabled: true,
        // What the document states. For a draw it is read off the draw on the server - the
        // approved total for a request, the disbursement for a ledger row - so what is shown here
        // is a preview and the server has the last word.
      },
      ...getStateExtraInputs(propertyStateCode, waiverType || ''),
      divider: {
        inputType: 'section',
        title: 'Exceptions',
      },
      exceptionReason: {
        inputType: 'text',
        title: 'This document does not affect any of the following',
        value: '',
        error: '',
      },
      extraAmount: {
        inputType: 'text',
        type: 'thousandSep',
        title: 'Disputed claims for extra Amount (Exception)',
        prefix: '$',
        value: '',
        error: '',
      },
    }
    return base
  }
  const [inputs, setInputs] = useState<any>(defaultInputs())

  const onOpen = () => {
    setInputs(defaultInputs())
  }

  const onClose = () => {
    onClosed?.()
  }

  const onChangeInput = async (key: string, value: any) => {
    const error = InputValidate({ ...inputs[key], value })
    let temp = cloneDeep(inputs)
    temp[key].value = value
    temp[key].error = error

    if (key === 'orderNo') {
      if (value === 'other') {
        temp['drawnAmount'].disabled = false
        temp['drawnAmount'].value = ''
        temp['drawnAmount'].error = REQUIRED_FIELD_ERROR_MESSAGE
      } else {
        const chosen = drawOptions.find((item) => optionKey(item) === value)
        if (chosen?.unavailable) {
          temp[key].error = chosen.unavailable
          temp['drawnAmount'].value = ''
          setInputs(temp)
          return
        }
        temp['drawnAmount'].disabled = true
        temp['drawnAmount'].value =
          chosen?.drawnAmount == null || chosen?.drawnAmount === ''
            ? ''
            : getPrice3decimal(removeComma(chosen.drawnAmount))
        temp['drawnAmount'].error = ''
      }
    }

    if (key === 'type') {
      const preserved: Record<string, any> = {
        type: value,
        orderNo: temp.orderNo?.value,
        drawnAmount: temp.drawnAmount?.value,
        drawnAmountDisabled: temp.drawnAmount?.disabled,
        exceptionReason: temp.exceptionReason?.value,
        extraAmount: temp.extraAmount?.value,
      }
      const rebuilt = defaultInputs(value)
      rebuilt.type.value = value
      rebuilt.orderNo.value = preserved.orderNo || ''
      rebuilt.drawnAmount.value = preserved.drawnAmount || ''
      rebuilt.drawnAmount.disabled = preserved.drawnAmountDisabled !== false && preserved.orderNo !== 'other'
      if (preserved.orderNo === 'other') rebuilt.drawnAmount.disabled = false
      rebuilt.exceptionReason.value = preserved.exceptionReason || ''
      rebuilt.extraAmount.value = preserved.extraAmount || ''
      // Restore any overlapping state field values
      Object.keys(rebuilt).forEach((k) => {
        if (temp[k]?.value != null && !['type', 'orderNo', 'drawnAmount', 'exceptionReason', 'extraAmount'].includes(k)) {
          if (rebuilt[k] && rebuilt[k].inputType !== 'section') rebuilt[k].value = temp[k].value
        }
      })
      setInputs(rebuilt)
      return
    }

    setInputs(temp)
  }

  const auth = useSelector((state: any) => state.auth)

  const onSubmit = async () => {
    let errors = 0
    let payload: any = {}
    let temp = cloneDeep(inputs)
    Object.keys(inputs).map((key) => {
      if (inputs[key].inputType === 'section') return
      payload[key] = inputs[key].value
      const error = InputValidate(inputs[key])
      if (error.length > 0) {
        temp[key].error = error
        errors += 1
      }
    })
    setInputs(temp)
    if (errors) {
      return toast(INVALID_ALL_INPUTS, { type: 'error' })
    }

    // The picker's value is one `source:id` key; it is split here and the composite deleted,
    // because the backend's validation pipe has no whitelist and would store it silently.
    const chosen = String(payload.orderNo || '')
    if (chosen && chosen !== 'other') {
      const [source, id] = chosen.split(':')
      if (source) {
        payload.drawSource = source
        payload.drawId = Number(id)
      }
    }

    if (onSubmitPayload) {
      setAction('submit')
      await onSubmitPayload(payload)
      setAction('')
      setIsOpen(false)
      setLastUpdatedAt(Date.now())
      return true
    }

    setAction('download')
    // The report route builds the PDF from the fields alone; the draw it belongs to is only
    // meaningful to a waiver being SENT, which is the branch above.
    delete payload.drawSource
    delete payload.drawId
    const res: Blob = await Api.get(
      `${appApiUrl}/reports/constructionLienWaiver?${serialize(payload)}&token=${auth.token}`,
      {},
      {},
      {
        responseType: 'blob',
      },
    )
    const loanNumber = Api.getLoanNumber()
    downloadFile(`${COMPANY_NAME_FC} Construction Lien Waiver - ${loanNumber}.pdf`, res)

    setAction('')
    setIsOpen(false)
    setLastUpdatedAt(Date.now())
  }

  return (
    <Modal
      button={
        props.button ?? (
          <button className="hover:underline text-[14px] text-shade-blue p-2">
            <div className="flex items-center">
              <DocumentArrowDownIcon className="w-4 h-4 mr-1" /> Construction Lien Waiver
            </div>
          </button>
        )
      }
      loading={action === 'download' || action === 'submit'}
      title={'Construction Lien Waiver'}
      titleOkay={titleOkay}
      isOpen={isOpen}
      lastUpdatedAt={lastUpdatedAt}
      onClose={onClose}
      onOk={onSubmit}
      onOpen={onOpen}
    >
      <div className="w-full max-w-[520px]">
        {propertyStateCode && (
          <p className="text-sm text-gray-500 mb-2">
            Using {propertyStateCode} state-specific lien waiver form
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          {Object.keys(inputs).map((key, index) => {
            let input = inputs[key]
            return (
              <div key={index} className={`md:col-span-${input.span || 1}`}>
                <RenderInput input={input} Key={key} onChange={onChangeInput} />
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}
